import { useEffect, useState } from "react";
import styled from "styled-components";
import ClipLoader from "react-spinners/ClipLoader";
import {
  mobileMaxWidth,
  colors,
  fonts,
  radii,
  transitions,
  spacing,
} from "../theme";
import AnimatedSection from "./AnimatedSection";

const Section = styled.section`
  background: ${colors.background};
  padding: 0 ${spacing.sectionX} ${spacing.sectionY};

  @media screen and (max-width: ${mobileMaxWidth}) {
    padding: 0 16px ${spacing.sectionYMobile};
  }
`;

const Card = styled.div`
  max-width: ${spacing.maxWidth};
  margin: 0 auto;
  display: grid;
  grid-template-columns: 5fr 7fr;
  background: ${colors.surface};
  border: 1px solid ${colors.border};
  border-radius: ${radii.xl};
  box-shadow: ${colors.shadowCard};
  overflow: hidden;

  @media screen and (max-width: ${mobileMaxWidth}) {
    grid-template-columns: 1fr;
  }
`;

const Intro = styled.div`
  position: relative;
  overflow: hidden;
  padding: 56px;
  background: ${colors.ink};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 40px;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
        400px circle at 0% 100%,
        rgba(33, 158, 188, 0.35),
        transparent 70%
      ),
      radial-gradient(
        400px circle at 100% 0%,
        rgba(59, 108, 246, 0.25),
        transparent 70%
      );
    pointer-events: none;
  }

  > * {
    position: relative;
  }

  @media screen and (max-width: ${mobileMaxWidth}) {
    padding: 40px 24px;
  }
`;

const Eyebrow = styled.p`
  margin: 0;
  color: ${colors.accentLight};
  font-family: ${fonts.body};
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Title = styled.h2`
  margin: 12px 0 0;
  font-family: ${fonts.heading};
  font-size: clamp(30px, 4vw, 40px);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.15;
  color: ${colors.white};
`;

const Body = styled.p`
  margin: 16px 0 0;
  font-family: ${fonts.body};
  font-size: 17px;
  line-height: 1.6;
  color: ${colors.textOnDarkMuted};
`;

const Perks = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: ${colors.textOnDark};
  font-family: ${fonts.body};
  font-size: 15px;

  li {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  li::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${colors.accent};
    box-shadow: 0 0 0 4px rgba(33, 158, 188, 0.25);
  }
`;

const ContactForm = styled.form`
  padding: 56px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media screen and (max-width: ${mobileMaxWidth}) {
    grid-template-columns: 1fr;
    padding: 32px 24px;
  }
`;

const Field = styled.div<{ wide?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  grid-column: ${({ wide }) => (wide ? "1 / -1" : "auto")};
`;

const BoxName = styled.label`
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 14px;
  color: ${colors.ink};
`;

const inputStyles = `
  width: 100%;
  padding: 14px 16px;
  font-family: ${fonts.body};
  font-size: 16px;
  color: ${colors.ink};
  background-color: ${colors.background};
  border-radius: ${radii.medium};
  outline: none;
  transition: ${transitions.default};

  ::placeholder {
    color: ${colors.textLight};
  }

  &:hover {
    background-color: ${colors.surfaceMuted};
  }

  &:focus {
    background-color: ${colors.surface};
    border-color: ${colors.accent};
    box-shadow: 0 0 0 4px rgba(33, 158, 188, 0.15);
  }
`;

const FormInput = styled.input<{ isValid: boolean }>`
  ${inputStyles}
  border: 1px solid ${(props) =>
    props.isValid ? colors.border : colors.error};
`;

const BigFormInput = styled.textarea<{ isValid: boolean }>`
  ${inputStyles}
  min-height: 140px;
  resize: vertical;
  border: 1px solid ${(props) => (props.isValid ? colors.border : colors.error)};
`;

const FormFooter = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
`;

const SubmitButton = styled.button`
  min-width: 200px;
  height: 52px;
  padding: 0 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${fonts.body};
  font-size: 16px;
  font-weight: 600;
  border-radius: ${radii.round};
  background: ${colors.ink};
  border: none;
  color: ${colors.white};
  cursor: pointer;
  transition: ${transitions.default};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${colors.shadowMedium};
  }

  &:disabled {
    opacity: 0.6;
    transform: none;
    box-shadow: none;
    cursor: not-allowed;
  }
`;

enum Email {
  not_sent,
  sending,
  sent,
  no_name,
  no_message,
  wrong_email,
  failed,
}

const EmailMessage = styled.div<{ emailStatus: Email }>`
  font-family: ${fonts.body};
  font-size: 15px;
  color: ${(props) =>
    props.emailStatus === Email.sent ? colors.success : colors.error};
  font-weight: 600;
`;

const ContactUsElement = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [contactForm, setContactForm] = useState(Email.not_sent);

  useEffect(() => {
    if (contactForm === Email.sent) {
      setName("");
      setEmail("");
      setMessage("");
    }
  }, [contactForm]);

  const sendContactForm = async () => {
    if (name === "") {
      setContactForm(Email.no_name);
      return;
    }
    if (!validateEmail(email)) {
      setContactForm(Email.wrong_email);
      return;
    }
    if (message === "") {
      setContactForm(Email.no_message);
      return;
    }

    setContactForm(Email.sending);

    try {
      const response = await fetch(
        process.env.REACT_APP_FIREBASE_CONTACTFORM_URL!,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({ name, email, message }).toString(),
        }
      );

      if (!response.ok) {
        throw new Error("Request failed");
      }

      setContactForm(Email.sent);
    } catch {
      setContactForm(Email.failed);
    }
  };

  const validateEmail = (email: string) => {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
  };

  return (
    <Section id="contact">
      <AnimatedSection>
        <Card>
          <Intro>
            <div>
              <Eyebrow>Kontakt</Eyebrow>
              <Title>Har du några frågor till devKey?</Title>
              <Body>
                Fyll i formuläret så återkommer vi så snart som möjligt!
              </Body>
            </div>
            <Perks>
              <li>Cross-platform apputveckling</li>
              <li>iOS och Android från en kodbas</li>
            </Perks>
          </Intro>
          <ContactForm
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              sendContactForm();
            }}
          >
            <Field>
              <BoxName htmlFor="name">Namn/Företag</BoxName>
              <FormInput
                isValid={contactForm !== Email.no_name}
                placeholder="Anna Andersson"
                id="name"
                autoComplete="name"
                value={name}
                onInput={(e) => setName(e.currentTarget.value)}
              />
            </Field>
            <Field>
              <BoxName htmlFor="email">Email</BoxName>
              <FormInput
                isValid={contactForm !== Email.wrong_email}
                placeholder="anna@foretag.se"
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onInput={(e) => setEmail(e.currentTarget.value)}
              />
            </Field>
            <Field wide>
              <BoxName htmlFor="message">Meddelande</BoxName>
              <BigFormInput
                isValid={contactForm !== Email.no_message}
                placeholder="Berätta kort om din idé eller fråga…"
                id="message"
                value={message}
                onInput={(e) => setMessage(e.currentTarget.value)}
              />
            </Field>
            <FormFooter>
              <SubmitButton
                id="submit"
                type="submit"
                disabled={contactForm === Email.sending}
              >
                {contactForm === Email.sending ? (
                  <ClipLoader
                    color={"#ffffff"}
                    size={20}
                    loading={contactForm === Email.sending}
                  />
                ) : (
                  "Skicka meddelande"
                )}
              </SubmitButton>
              <div aria-live="polite">
                {contactForm === Email.sent && (
                  <EmailMessage emailStatus={contactForm}>
                    Tack för ditt meddelande!
                  </EmailMessage>
                )}
                {contactForm === Email.failed && (
                  <EmailMessage emailStatus={contactForm}>
                    Ett fel uppstod när meddelandet skulle skickas.
                  </EmailMessage>
                )}
                {contactForm === Email.no_name && (
                  <EmailMessage emailStatus={contactForm}>
                    Vänligen fyll i namn/företag.
                  </EmailMessage>
                )}
                {contactForm === Email.no_message && (
                  <EmailMessage emailStatus={contactForm}>
                    Vänligen fyll meddelande.
                  </EmailMessage>
                )}
                {contactForm === Email.wrong_email && (
                  <EmailMessage emailStatus={contactForm}>
                    Vänligen fyll i en giltig email.
                  </EmailMessage>
                )}
              </div>
            </FormFooter>
          </ContactForm>
        </Card>
      </AnimatedSection>
    </Section>
  );
};

export default ContactUsElement;
